"use client";

import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import { Button } from "@/shadcn-components/ui/button";
import { Checkbox } from "@/shadcn-components/ui/checkbox";
import { useSettingsStore } from "@/lib/settings";
import { cn } from "@/lib/utils";

/** Order the agent wants to place, laid out like a trade ticket. */
export interface AgentOrderConsentRequest {
  action: "BUY" | "SELL";
  symbol: string;
  volume: number;
  stopLoss?: number;
  takeProfit?: number;
}

interface AgentOrderConsentDialogProps {
  open: boolean;
  order: AgentOrderConsentRequest | null;
  onApprove: () => void;
  onReject: () => void;
}

function TicketRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: "buy" | "sell";
}) {
  return (
    <div className="flex items-center justify-between border-b border-border px-2.5 py-1.5 font-mono text-[11.5px] last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          "text-foreground",
          tone === "buy" && "text-[color:var(--price-up,#60a5fa)]",
          tone === "sell" && "text-[color:var(--price-down,#f87171)]",
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * One-time consent gate for agent-placed orders. Mirrors OneClickTradingDialog in spirit:
 * a "don't ask again" checkbox persists agentTradingConsent so future agent orders fill
 * without a prompt. Reject fails the tool call closed.
 */
export function AgentOrderConsentDialog({
  open,
  order,
  onApprove,
  onReject,
}: AgentOrderConsentDialogProps) {
  const { updateSetting } = useSettingsStore();
  const [remember, setRemember] = useState(false);

  const handleApprove = () => {
    if (remember) {
      updateSetting("agentTradingConsent", true);
    }
    onApprove();
  };

  const handleOpenChange = (next: boolean) => {
    // Any dismissal (Esc / backdrop) is a rejection — fail closed.
    if (!next) onReject();
  };

  const isBuy = order?.action === "BUY";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Agent wants to place an order</DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-2 font-mono text-[11.5px] text-[color:var(--yellow,#eab308)]">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>
            The Vybe Agent is about to send a{" "}
            <span className="font-semibold text-foreground">live order</span> to
            your connected account. Review before approving.
          </span>
        </div>

        <div className="rounded-md border border-border">
          <TicketRow
            label="Action"
            value={`${order?.action ?? "—"} · market`}
            tone={isBuy ? "buy" : "sell"}
          />
          <TicketRow label="Symbol" value={order?.symbol ?? "—"} />
          <TicketRow
            label="Volume"
            value={order ? `${order.volume} lots` : "—"}
          />
          <TicketRow
            label="Stop loss"
            value={order?.stopLoss != null ? order.stopLoss : "—"}
          />
          <TicketRow
            label="Take profit"
            value={order?.takeProfit != null ? order.takeProfit : "—"}
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2.5 text-xs text-muted-foreground">
          <Checkbox
            checked={remember}
            onCheckedChange={(v) => setRemember(v === true)}
          />
          Don&apos;t ask again — let the agent place orders on this account
        </label>

        <DialogFooter className="flex gap-3">
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={onReject}
          >
            Reject
          </Button>
          <Button onClick={handleApprove}>Approve &amp; send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
